import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, forkJoin, tap, map, catchError } from 'rxjs';
import { getConfig } from '../config.service';
import {
  ISeverityLevel,
  IDefectState,
  ICategory,
  ICategory2,
  ICategory3,
  IBuildingManager,
  IEmployee,
} from './defect.models';

/**
 * Service for loading and caching defect codebook data.
 * Migrated from web-app-legacy/js/app/Components/Common/CodeBookService.ts
 */
@Injectable({
  providedIn: 'root',
})
export class CodebookService {
  private http = inject(HttpClient);
  private get baseUrl() { return getConfig().api.middlewareUrl; }

  // Cached data
  private severityLevels: ISeverityLevel[] = [];
  private defectStates: IDefectState[] = [];
  private categoriesLevel1: ICategory[] = [];
  private categoriesLevel2: ICategory2[] = [];
  private categoriesLevel3: ICategory3[] = [];
  private buildingManagers: IBuildingManager[] = [];
  private allManagers: IEmployee[] = [];
  private allCoordinators: IEmployee[] = [];

  /**
   * Load all codebooks needed for defect editor.
   */
  loadAllCodebooks(): Observable<void> {
    return forkJoin([
      this.loadSeverityLevels(),
      this.loadDefectStates(),
      this.loadCategories1(),
      this.loadCategories2(),
      this.loadCategories3(),
    ]).pipe(map((): void => void 0));
  }

  /**
   * Load severity levels.
   */
  loadSeverityLevels(): Observable<ISeverityLevel[]> {
    if (this.severityLevels.length > 0) {
      return of(this.severityLevels);
    }

    return this.http.get<ISeverityLevel[]>(`${this.baseUrl}/api/Codebook/SeverityLevel`).pipe(
      tap((data) => {
        this.severityLevels = data;
      }),
      catchError((error) => {
        console.error('[CodebookService] Error loading severity levels:', error);
        return of([]);
      })
    );
  }

  /**
   * Load defect states.
   */
  loadDefectStates(): Observable<IDefectState[]> {
    if (this.defectStates.length > 0) {
      return of(this.defectStates);
    }

    return this.http.get<IDefectState[]>(`${this.baseUrl}/api/Codebook/DefectState`).pipe(
      tap((data) => {
        this.defectStates = data;
      }),
      catchError((error) => {
        console.error('[CodebookService] Error loading defect states:', error);
        return of([]);
      })
    );
  }

  /**
   * Load first level categories.
   */
  loadCategories1(): Observable<ICategory[]> {
    if (this.categoriesLevel1.length > 0) {
      return of(this.categoriesLevel1);
    }

    return this.http.get<ICategory[]>(`${this.baseUrl}/api/Codebook/Category1`).pipe(
      tap((data) => {
        this.categoriesLevel1 = data;
      }),
      catchError((error) => {
        console.error('[CodebookService] Error loading categories1:', error);
        return of([]);
      })
    );
  }

  /**
   * Load second level categories.
   */
  loadCategories2(): Observable<ICategory2[]> {
    if (this.categoriesLevel2.length > 0) {
      return of(this.categoriesLevel2);
    }

    return this.http.get<ICategory2[]>(`${this.baseUrl}/api/Codebook/Category2/`).pipe(
      tap((data) => {
        this.categoriesLevel2 = data;
      }),
      catchError((error) => {
        console.error('[CodebookService] Error loading categories2:', error);
        return of([]);
      })
    );
  }

  /**
   * Load third level categories.
   */
  loadCategories3(): Observable<ICategory3[]> {
    if (this.categoriesLevel3.length > 0) {
      return of(this.categoriesLevel3);
    }

    return this.http.get<ICategory3[]>(`${this.baseUrl}/api/Codebook/Category3/`).pipe(
      tap((data) => {
        this.categoriesLevel3 = data;
      }),
      catchError((error) => {
        console.error('[CodebookService] Error loading categories3:', error);
        return of([]);
      })
    );
  }

  /**
   * Load building managers for a site.
   */
  loadBuildingManagers(siteId: number): Observable<IBuildingManager[]> {
    return this.http.get<IBuildingManager[]>(`${this.baseUrl}/api/fms/buildingmanagers/${siteId}`).pipe(
      tap((data) => {
        this.buildingManagers = data;
      }),
      catchError((error) => {
        console.error('[CodebookService] Error loading building managers:', error);
        return of([]);
      })
    );
  }

  // Getters for cached data
  getSeverityLevels(): ISeverityLevel[] {
    return this.severityLevels;
  }

  getDefectStates(): IDefectState[] {
    return this.defectStates;
  }

  getCategories1(): ICategory[] {
    return this.categoriesLevel1;
  }

  getCategories2(): ICategory2[] {
    return this.categoriesLevel2;
  }

  getCategories3(): ICategory3[] {
    return this.categoriesLevel3;
  }

  getBuildingManagers(): IBuildingManager[] {
    return this.buildingManagers;
  }

  // Helper methods for filtering

  /**
   * Get severity level by ID.
   */
  getSeverityById(id: number): ISeverityLevel | null {
    return this.severityLevels.find((s) => s.id === id) ?? null;
  }

  /**
   * Get defect state by ID.
   */
  getDefectStateById(id: number): IDefectState | null {
    return this.defectStates.find((s) => s.id === id) ?? null;
  }

  /**
   * Get category 1 by ID.
   */
  getCategory1ById(id: number): ICategory | null {
    return this.categoriesLevel1.find((c) => c.id === id) ?? null;
  }

  /**
   * Get category 2 by ID.
   */
  getCategory2ById(id: number): ICategory2 | null {
    return this.categoriesLevel2.find((c) => c.id === id) ?? null;
  }

  /**
   * Get category 3 by ID.
   */
  getCategory3ById(id: number): ICategory3 | null {
    return this.categoriesLevel3.find((c) => c.id === id) ?? null;
  }

  /**
   * Get categories2 filtered by parent category1 ID.
   */
  getCategory2ByParentId(category1Id: number): ICategory2[] {
    return this.categoriesLevel2.filter((c) => c.category1Id === category1Id);
  }

  /**
   * Get categories3 filtered by parent category2 ID.
   */
  getCategory3ByParentId(category2Id: number): ICategory3[] {
    return this.categoriesLevel3.filter((c) => c.category2Id === category2Id);
  }

  /**
   * Get building managers by building ID.
   */
  getManagersByBuildingId(buildingId: number): IBuildingManager[] {
    return this.buildingManagers.filter((m) => m.buildingId === buildingId);
  }

  /**
   * Get categories1 filtered by outdoor/indoor flag.
   */
  getCategories1ByLocation(isOutdoor: boolean): ICategory[] {
    return this.categoriesLevel1.filter((c) => c.isOutdoor === isOutdoor);
  }

  /**
   * Load all managers for filter dropdown.
   * GET /api/fms/managers/{siteId}
   */
  loadAllManagers(siteId: number): Observable<IEmployee[]> {
    if (this.allManagers.length > 0) {
      return of(this.allManagers);
    }

    return this.http.get<IEmployee[]>(`${this.baseUrl}/api/fms/managers/${siteId}`).pipe(
      tap((data) => {
        this.allManagers = data;
      }),
      catchError((error) => {
        console.error('[CodebookService] Error loading all managers:', error);
        return of([]);
      })
    );
  }

  /**
   * Load all coordinators for filter dropdown.
   * GET /api/fms/coordinators/{siteId}
   */
  loadAllCoordinators(siteId: number): Observable<IEmployee[]> {
    if (this.allCoordinators.length > 0) {
      return of(this.allCoordinators);
    }

    return this.http.get<IEmployee[]>(`${this.baseUrl}/api/fms/coordinators/${siteId}`).pipe(
      tap((data) => {
        this.allCoordinators = data;
      }),
      catchError((error) => {
        console.error('[CodebookService] Error loading all coordinators:', error);
        return of([]);
      })
    );
  }

  /**
   * Get cached managers.
   */
  getAllManagers(): IEmployee[] {
    return this.allManagers;
  }

  /**
   * Get cached coordinators.
   */
  getAllCoordinators(): IEmployee[] {
    return this.allCoordinators;
  }

  /**
   * Clear cached filter data (call when site changes).
   */
  clearFilterCache(): void {
    this.allManagers = [];
    this.allCoordinators = [];
  }
}
